import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { CreateNewFolder as FolderIcon } from '@mui/icons-material';
import { SpreadsheetDocument, SpreadsheetGridInput } from 'models/SpreadsheetDocument';
import { importSpreadsheetFolder, SpreadsheetFolderImportResult } from './importSpreadsheetFolder';

interface FolderSpreadsheetUploadProps {
  disabled: boolean;
  allSheets?: boolean;
  importGrid?: (grid: SpreadsheetGridInput) => SpreadsheetGridInput | string;
  onImport: (documents: SpreadsheetDocument[]) => void;
  onBusyChange: (busy: boolean) => void;
}

/** 폴더 내 엑셀을 일괄 등록하고, 여러 시트가 있는 파일은 파일별로 시트를 선택한다. */
const FolderSpreadsheetUpload: React.FC<FolderSpreadsheetUploadProps> = ({
  disabled,
  allSheets = false,
  importGrid,
  onImport,
  onBusyChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const resolveSheetRef = useRef<((sheetName: string | null) => void) | null>(null);
  const [progress, setProgress] = useState<{ completed: number; total: number } | null>(null);
  const [pending, setPending] = useState<{ path: string; sheetNames: string[] } | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [result, setResult] = useState<SpreadsheetFolderImportResult | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      resolveSheetRef.current?.(null);
    },
    [],
  );

  const finishSheetSelection = (selected: string | null) => {
    const resolve = resolveSheetRef.current;
    resolveSheetRef.current = null;
    setPending(null);
    setSheetName('');
    resolve?.(selected);
  };

  const handleFolderPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (disabled || progress || files.length === 0) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setResult(null);
    onBusyChange(true);
    try {
      const imported = await importSpreadsheetFolder(files, {
        importGrid,
        allSheets,
        signal: controller.signal,
        onProgress: (completed, total) => setProgress({ completed, total }),
        selectSheet: (path, sheetNames) =>
          new Promise((resolve) => {
            resolveSheetRef.current = resolve;
            setSheetName(sheetNames[0]);
            setPending({ path, sheetNames });
          }),
      });
      if (controller.signal.aborted) return;
      if (imported.documents.length > 0) onImport(imported.documents);
      setResult(imported);
    } finally {
      if (!controller.signal.aborted) {
        setProgress(null);
        onBusyChange(false);
      }
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".xlsx,.xls"
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        aria-label="엑셀 폴더 선택"
        style={{ display: 'none' }}
        disabled={disabled || !!progress}
        onChange={handleFolderPick}
      />
      <Button
        variant="outlined"
        startIcon={progress ? <CircularProgress size={14} color="inherit" /> : <FolderIcon />}
        disabled={disabled || !!progress}
        onClick={() => inputRef.current?.click()}
      >
        {progress ? `가져오는 중 ${progress.completed}/${progress.total}` : '폴더 업로드'}
      </Button>

      <Dialog open={!!pending} onClose={() => finishSheetSelection(null)} maxWidth="sm" fullWidth>
        <DialogTitle>폴더 업로드: 가져올 시트 선택</DialogTitle>
        <DialogContent>
          <Typography fontWeight={700} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
            {pending?.path}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            여러 시트가 있는 파일입니다. 이 파일의 문서로 등록할 시트를 선택하세요.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="folder-sheet-select-label">시트</InputLabel>
            <Select
              labelId="folder-sheet-select-label"
              label="시트"
              value={sheetName}
              onChange={(event) => setSheetName(event.target.value)}
            >
              {pending?.sheetNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => finishSheetSelection(null)}>이 파일 건너뛰기</Button>
          <Button
            variant="contained"
            disabled={!sheetName}
            onClick={() => finishSheetSelection(sheetName)}
          >
            가져오기
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!result} onClose={() => setResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>폴더 업로드 결과</DialogTitle>
        <DialogContent>
          {result && (
            <Stack spacing={2}>
              <Alert
                severity={
                  result.issues.length > 0 || result.documents.length === 0 ? 'warning' : 'success'
                }
              >
                {result.documents.length > 0
                  ? `문서 ${result.documents.length}개를 추가했습니다. 변경 내용은 자동 저장됩니다.`
                  : '추가된 문서가 없습니다.'}
              </Alert>
              {result.ignoredFiles > 0 && (
                <Typography variant="body2" color="text.secondary">
                  엑셀이 아닌 파일 및 임시 파일 {result.ignoredFiles}개는 제외했습니다. (.xls, .xlsx
                  지원)
                </Typography>
              )}
              {result.issues.length > 0 && (
                <Box>
                  <Typography fontWeight={700}>
                    등록하지 못한 파일 {result.issues.length}개
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                    {result.issues.map((issue) => (
                      <Box component="li" key={issue.path} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
                        <Typography variant="body2" fontWeight={700}>
                          {issue.path}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {issue.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResult(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FolderSpreadsheetUpload;
